/**
 * Test di misura: metodi di calcolo, parametri, risultati e contratti.
 *
 * Un test ha una o più misure; ognuna converte un risultato grezzo
 * (centimetri, secondi, livello e navetta…) in un punteggio 1–100, lo stesso
 * per tutti gli attributi che valuta. Le formule sono in
 * docs/sviluppi/test-misura/formule.md, i test composti in
 * docs/sviluppi/test-composti: le formule le usano
 * l'anteprima del form, la vista di esecuzione e il server che salva, quindi
 * stanno qui una volta sola.
 */

import { z } from 'zod';
import { idSchema, normalizzaNome } from './contratti.js';
import { minutiRiferimentoSchema, rpeRiferimentoSchema } from './schede.js';

export const METODI_TEST = [
  'percentuale',
  'diretta',
  'inversa',
  'target',
  'gradini',
  'penalita',
] as const;
export type MetodoTest = (typeof METODI_TEST)[number];

export const ETICHETTA_METODO_TEST: Record<MetodoTest, string> = {
  percentuale: 'Percentuale',
  diretta: 'Diretta (più è meglio)',
  inversa: 'Inversa (meno è meglio)',
  target: 'Target zone',
  gradini: 'A gradini',
  penalita: 'Con penalità',
};

// ---------------------------------------------------------------------------
// Numeri
// ---------------------------------------------------------------------------

/** Al più tre decimali: i tempi si prendono al centesimo, i pesi al grammo. */
const alPiuTreDecimali = (v: number) => Math.abs(v * 1000 - Math.round(v * 1000)) < 1e-6;

const numero = (nome: string) =>
  z
    .number({
      invalid_type_error: `${nome}: inserisci un numero.`,
      required_error: `${nome}: obbligatorio.`,
    })
    .finite(`${nome}: inserisci un numero.`)
    .refine(alPiuTreDecimali, `${nome}: al più tre decimali.`);

const positivo = (nome: string) =>
  numero(nome).refine((v) => v > 0, `${nome}: deve essere maggiore di 0.`);
const nonNegativo = (nome: string) =>
  numero(nome).refine((v) => v >= 0, `${nome}: non può essere negativo.`);
const intero = (nome: string, minimo: number) =>
  z
    .number({
      invalid_type_error: `${nome}: inserisci un numero.`,
      required_error: `${nome}: obbligatorio.`,
    })
    .int(`${nome}: solo numeri interi.`)
    .min(minimo, minimo === 0 ? `${nome}: non può essere negativo.` : `${nome}: almeno ${minimo}.`);

// ---------------------------------------------------------------------------
// Parametri
// ---------------------------------------------------------------------------

const parametriPercentuale = z.object({
  metodo: z.literal('percentuale'),
  massimo: positivo('Massimo'),
});

const parametriDiretta = z.object({
  metodo: z.literal('diretta'),
  minimo: numero('Minimo'),
  massimo: numero('Massimo'),
});

const parametriInversa = z.object({
  metodo: z.literal('inversa'),
  migliore: nonNegativo('Migliore'),
  peggiore: nonNegativo('Peggiore'),
});

const parametriTarget = z.object({
  metodo: z.literal('target'),
  target: numero('Target'),
  tolleranza: positivo('Tolleranza'),
});

const parametriGradini = z.object({
  metodo: z.literal('gradini'),
  lunghezzaNavetta: positivo('Lunghezza navetta'),
  /** Un numero vale per ogni livello; più numeri, uno per livello dal primo. */
  navettePerLivello: z
    .array(intero('Navette per livello', 1), {
      invalid_type_error: 'Navette per livello non valide.',
    })
    .min(1, 'Indica le navette per livello.')
    .max(100, 'Al più 100 livelli.'),
  metriMinimi: nonNegativo('Metri minimi'),
  metriMassimi: nonNegativo('Metri massimi'),
});

const parametriPenalita = z.object({
  metodo: z.literal('penalita'),
  penalita: nonNegativo('Penalità'),
  migliore: nonNegativo('Migliore'),
  peggiore: nonNegativo('Peggiore'),
});

/** Parametri del test, uno schema per metodo. */
export const parametriTestSchema = z
  .discriminatedUnion(
    'metodo',
    [
      parametriPercentuale,
      parametriDiretta,
      parametriInversa,
      parametriTarget,
      parametriGradini,
      parametriPenalita,
    ],
    { errorMap: () => ({ message: 'Metodo di calcolo non valido.' }) },
  )
  .superRefine((p, ctx) => {
    const minore = (a: number, b: number, path: string, message: string) => {
      if (!(a < b)) ctx.addIssue({ code: 'custom', path: [path], message });
    };
    if (p.metodo === 'diretta') {
      minore(p.minimo, p.massimo, 'massimo', 'Il minimo deve essere minore del massimo.');
    }
    if (p.metodo === 'inversa' || p.metodo === 'penalita') {
      minore(p.migliore, p.peggiore, 'peggiore', 'Il migliore deve essere minore del peggiore.');
    }
    if (p.metodo === 'gradini') {
      minore(
        p.metriMinimi,
        p.metriMassimi,
        'metriMassimi',
        'I metri minimi devono essere meno dei massimi.',
      );
    }
  });

export type ParametriTest = z.infer<typeof parametriTestSchema>;
type Parametri<M extends MetodoTest> = Extract<ParametriTest, { metodo: M }>;

// ---------------------------------------------------------------------------
// Risultati
// ---------------------------------------------------------------------------

/** Navette del livello (da 1), o `undefined` se il livello non esiste. */
function navetteDelLivello(p: Parametri<'gradini'>, livello: number): number | undefined {
  const lista = p.navettePerLivello;
  return lista.length === 1 ? lista[0] : lista[livello - 1];
}

/**
 * Schema del risultato di un giocatore per quel test. Dipende dai parametri:
 * nel gradini il livello e la navetta si controllano contro la lista.
 */
export function risultatoTestSchema(p: ParametriTest) {
  switch (p.metodo) {
    case 'percentuale':
    case 'inversa':
      return z.object({ valore: nonNegativo('Risultato') }).strict();
    case 'diretta':
    case 'target':
      return z.object({ valore: numero('Risultato') }).strict();
    case 'penalita':
      return z.object({ tempo: nonNegativo('Tempo'), errori: intero('Errori', 0) }).strict();
    case 'gradini':
      return z
        .object({ livello: intero('Livello', 1), navetta: intero('Navetta', 0) })
        .strict()
        .superRefine((r, ctx) => {
          const navette = navetteDelLivello(p, r.livello);
          if (navette === undefined) {
            ctx.addIssue({
              code: 'custom',
              path: ['livello'],
              message: `Il test ha ${p.navettePerLivello.length} livelli.`,
            });
          } else if (r.navetta > navette) {
            ctx.addIssue({
              code: 'custom',
              path: ['navetta'],
              message: `Il livello ${r.livello} ha ${navette} navette.`,
            });
          }
        });
  }
}

export type RisultatoTest =
  { valore: number } | { tempo: number; errori: number } | { livello: number; navetta: number };

// ---------------------------------------------------------------------------
// Calcolo
// ---------------------------------------------------------------------------

/** Metri percorsi: navette dei livelli completati più quelle dell'ultimo. */
export function metriGradini(p: Parametri<'gradini'>, livello: number, navetta: number): number {
  let navette = navetta;
  for (let l = 1; l < livello; l++) navette += navetteDelLivello(p, l) ?? 0;
  return navette * p.lunghezzaNavetta;
}

/** Tempo con gli errori pagati in secondi. */
export function tempoNetto(p: Parametri<'penalita'>, tempo: number, errori: number): number {
  return tempo + errori * p.penalita;
}

const diretta = (v: number, minimo: number, massimo: number) =>
  ((v - minimo) / (massimo - minimo)) * 100;
const inversa = (v: number, migliore: number, peggiore: number) =>
  ((peggiore - v) / (peggiore - migliore)) * 100;

/**
 * Intero più vicino, la metà in su, fra 1 e 100. Il passaggio a sei decimali
 * toglie il rumore della virgola mobile: 99 ÷ 200 × 100 deve fare 49,5.
 */
function scala(p: number): number {
  const netto = Math.round(p * 1e6) / 1e6;
  return Math.min(100, Math.max(1, Math.floor(netto + 0.5)));
}

export interface Punteggio {
  punteggio: number;
  /** Il valore intermedio da mostrare: metri (gradini), tempo netto (penalità). */
  derivato?: number;
}

/**
 * Punteggio 1–100 di un risultato già validato con `risultatoTestSchema`.
 * Un risultato della forma sbagliata è un errore di programma, non d'utente.
 */
export function calcolaPunteggio(p: ParametriTest, r: Record<string, number>): Punteggio {
  const campo = (nome: string) => {
    const v = r[nome];
    if (typeof v !== 'number') throw new Error(`Risultato senza «${nome}».`);
    return v;
  };
  switch (p.metodo) {
    case 'percentuale':
      return { punteggio: scala((campo('valore') / p.massimo) * 100) };
    case 'diretta':
      return { punteggio: scala(diretta(campo('valore'), p.minimo, p.massimo)) };
    case 'inversa':
      return { punteggio: scala(inversa(campo('valore'), p.migliore, p.peggiore)) };
    case 'target':
      return {
        punteggio: scala(100 - (Math.abs(campo('valore') - p.target) / p.tolleranza) * 100),
      };
    case 'gradini': {
      const metri = metriGradini(p, campo('livello'), campo('navetta'));
      return { punteggio: scala(diretta(metri, p.metriMinimi, p.metriMassimi)), derivato: metri };
    }
    case 'penalita': {
      const netto = tempoNetto(p, campo('tempo'), campo('errori'));
      return { punteggio: scala(inversa(netto, p.migliore, p.peggiore)), derivato: netto };
    }
  }
}

// ---------------------------------------------------------------------------
// Contratti
// ---------------------------------------------------------------------------

const testoFacoltativo = (massimo: number) =>
  z
    .string()
    .max(massimo, `Massimo ${massimo} caratteri.`)
    .nullish()
    .transform((t) => (t?.trim() ? t.trim() : null));

const attributiSchema = z
  .array(idSchema)
  .min(1, 'Scegli almeno un attributo.')
  .refine((a) => new Set(a).size === a.length, "L'attributo è ripetuto.");

/**
 * Una misura del test: i parametri portano il metodo. L'`id` c'è quando si
 * modifica una misura che esiste già.
 */
export const misuraTestSchema = z.object({
  id: idSchema.optional(),
  nome: z
    .string()
    .trim()
    .min(1, 'Il nome è obbligatorio.')
    .max(60, 'Massimo 60 caratteri.')
    .transform(normalizzaNome),
  unita: testoFacoltativo(20),
  parametri: parametriTestSchema,
  attributi: attributiSchema,
});

export type DatiMisuraTest = z.infer<typeof misuraTestSchema>;

/**
 * Nuovo test o test modificato. RPE e minuti di riferimento servono a
 * metterlo in seduta come blocco: entrambi o nessuno. Un attributo sta in
 * una misura sola del test.
 */
export const creaTestSchema = z
  .object({
    nome: z
      .string()
      .trim()
      .min(1, 'Il nome è obbligatorio.')
      .max(80, 'Massimo 80 caratteri.')
      .transform(normalizzaNome),
    istruzioni: testoFacoltativo(2000),
    rpeRiferimento: rpeRiferimentoSchema.nullish().transform((n) => n ?? null),
    minutiRiferimento: minutiRiferimentoSchema.nullish().transform((n) => n ?? null),
    misure: z
      .array(misuraTestSchema)
      .min(1, 'Aggiungi almeno una misura.')
      .max(10, 'Massimo 10 misure.'),
  })
  .superRefine((d, ctx) => {
    if (d.rpeRiferimento !== null && d.minutiRiferimento === null) {
      ctx.addIssue({
        code: 'custom',
        path: ['minutiRiferimento'],
        message: 'Con l’RPE servono anche i minuti.',
      });
    }
    if (d.rpeRiferimento === null && d.minutiRiferimento !== null) {
      ctx.addIssue({
        code: 'custom',
        path: ['rpeRiferimento'],
        message: 'Con i minuti serve anche l’RPE.',
      });
    }
    const ids = new Set<string>();
    const nomi = new Set<string>();
    const attributi = new Set<string>();
    d.misure.forEach((m, i) => {
      if (m.id) {
        if (ids.has(m.id)) {
          ctx.addIssue({
            code: 'custom',
            path: ['misure', i, 'id'],
            message: 'La misura è ripetuta.',
          });
        }
        ids.add(m.id);
      }
      const nome = m.nome.toLocaleLowerCase('it');
      if (nomi.has(nome)) {
        ctx.addIssue({
          code: 'custom',
          path: ['misure', i, 'nome'],
          message: 'Il nome è già di un’altra misura.',
        });
      }
      nomi.add(nome);
      m.attributi.forEach((a, j) => {
        if (attributi.has(a)) {
          ctx.addIssue({
            code: 'custom',
            path: ['misure', i, 'attributi', j],
            message: "L'attributo è già in un'altra misura.",
          });
        }
      });
      m.attributi.forEach((a) => attributi.add(a));
    });
  });

export type DatiTest = z.infer<typeof creaTestSchema>;

/**
 * Esecuzione di un test: per ogni giocatore i risultati delle misure fatte,
 * per id di misura. La forma di ogni risultato si valida sul server con
 * `risultatoTestSchema`, contro i parametri copiati nell'esecuzione: qui si
 * controlla solo che sia fatto di numeri.
 */
export const esecuzioneTestSchema = z.object({
  data: z.string().date('Data non valida.'),
  righe: z
    .array(
      z.object({
        giocatoreId: idSchema,
        risultati: z
          .record(
            idSchema,
            z.record(z.string(), z.number({ invalid_type_error: 'Inserisci un numero.' })),
          )
          .refine((r) => Object.keys(r).length > 0, 'Inserisci almeno una misura.'),
      }),
    )
    .min(1, 'Inserisci almeno un risultato.')
    .refine(
      (r) => new Set(r.map((x) => x.giocatoreId)).size === r.length,
      'Il giocatore è ripetuto.',
    ),
});

export type DatiEsecuzioneTest = z.infer<typeof esecuzioneTestSchema>;

export const statoTestSchema = z.object({ attivo: z.boolean() });

const metodoQuery = z.union([z.enum(METODI_TEST), z.array(z.enum(METODI_TEST))]);

/** Filtri dell'elenco dei test, dalla query string. */
export const filtriTestSchema = z
  .object({
    metodo: metodoQuery.optional(),
    q: z.string().optional(),
    attivo: z.enum(['true', 'false']).optional(),
  })
  .transform((f) => ({
    ...(f.metodo && { metodo: Array.isArray(f.metodo) ? f.metodo : [f.metodo] }),
    ...(f.q?.trim() && { q: f.q.trim() }),
    ...(f.attivo !== undefined && { attivo: f.attivo === 'true' }),
  }));

export type FiltriTest = z.output<typeof filtriTestSchema>;
